var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StylePropType from 'react-style-proptype';
import enhanceWithClickOutside from 'react-onclickoutside';
import Tether from 'react-tether';
import debounce from 'lodash.debounce';
import { cx } from 'emotion';
import { deprecatedProp as deprecated } from './utils';
const attachments = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'middle right',
    right: 'middle left',
};
const targetAttachments = {
    top: 'top center',
    bottom: 'bottom center',
    left: 'middle left',
    right: 'middle right',
};
class Tooltip extends Component {
    constructor(props) {
        super(props);
        this.el = null;
        this.state = {
            show: this.props.show || false,
            // only used to prevent Tether from rendering on the first render due to perf issues.
            showInitial: this.props.show || false,
        };
        this.setShowInitial = () => {
            this.setState({ showInitial: true });
        };
        this.setShowFlag = value => {
            const show = this.props.show !== undefined ? this.props.show : value;
            this.toggleTether(show);
            this.setState({ show });
        };
        this.toggleTether = enable => {
            if (this.tether && this.tether.getTetherInstance()) {
                if (enable) {
                    this.tether.enable();
                    this.tether.position();
                }
                else {
                    this.tether.disable();
                }
            }
        };
        this.open = () => {
            this.debouncedClose.cancel();
            this.setShowFlag(true);
        };
        this.close = () => {
            this.debouncedOpen.cancel();
            this.setShowFlag(false);
        };
        this.handleClickOutside = e => {
            // don't count clicks in the tooltip as "outside" clicks
            if (this.el && this.el.current.contains(e.target)) {
                return;
            }
            this.props.onClickOutside && this.props.onClickOutside();
        };
        /* eslint-disable no-return-assign */
        this.attachTether = tether => (this.tether = tether);
        this.attachElement = el => (this.el = el);
        this.debouncedClose = debounce(this.close, 50);
        this.debouncedOpen = debounce(this.open, props.delay);
    }
    componentDidMount() {
        if (!this.state.show) {
            this.toggleTether(false);
        }
    }
    // Necessary since rerendering wipes the knowledge of the tether being disabled
    componentDidUpdate() {
        if (!this.state.show) {
            this.toggleTether(false);
        }
    }
    // If show is passed in, it should always use that value.
    static getDerivedStateFromProps(nextProps) {
        if (nextProps.show !== undefined) {
            return { show: nextProps.show };
        }
        return null;
    }
    /* eslint-enable no-return-assign */
    render() {
        const _a = this.props, { contents, direction, style, className, containerClassName, variety, variant, tooltipStyle, tooltipInnerStyle, constraints } = _a, rest = __rest(_a, ["contents", "direction", "style", "className", "containerClassName", "variety", "variant", "tooltipStyle", "tooltipInnerStyle", "constraints"]);
        const { show, showInitial } = this.state;
        const isPopover = (variant || variety) === 'popover';
        const popover = isPopover ? { display: 'block' } : {};
        if (showInitial || show) {
            return (React.createElement(Tether, { ref: this.attachTether, classPrefix: "tether-tooltip", attachment: attachments[direction], targetAttachment: targetAttachments[direction], constraints: constraints, renderTarget: ref => (React.createElement("div", { ref: ref, style: Object.assign({ display: 'inline-block' }, style), onMouseEnter: this.debouncedOpen, onMouseOver: this.debouncedOpen, onFocus: this.debouncedOpen, onMouseOut: this.debouncedClose, onMouseLeave: this.debouncedClose, onBlur: this.debouncedClose, className: cx(containerClassName, 'tooltip-container') }, this.props.children)), renderElement: ref => (React.createElement("div", { ref: this.attachElement(ref), className: cx(variant || variety || 'tooltip', className, { in: show }), role: "tooltip", style: Object.assign(Object.assign({ visibility: show ? 'visible' : 'hidden' }, popover), tooltipStyle), onMouseEnter: this.debouncedOpen, onMouseOver: this.debouncedOpen, onFocus: this.debouncedOpen, onMouseOut: this.debouncedClose, onMouseLeave: this.debouncedClose, onBlur: this.debouncedClose },
                    React.createElement("div", { className: (variant || variety || 'tooltip') === 'tooltip' ? 'tooltip-arrow' : 'arrow' }),
                    React.createElement("div", { className: isPopover ? 'popover-content' : 'tooltip-inner', style: Object.assign({}, tooltipInnerStyle) }, contents))) }));
        }
        const { onClickOutside, disableOnClickOutside, enableOnClickOutside, stopPropagation, preventDefault, outsideClickIgnoreClass, eventTypes, show: showProp } = rest, restProps = __rest(rest, ["onClickOutside", "disableOnClickOutside", "enableOnClickOutside", "stopPropagation", "preventDefault", "outsideClickIgnoreClass", "eventTypes", "show"]);
        return (React.createElement("div", Object.assign({ style: Object.assign({ display: 'inline-block' }, style), onMouseEnter: this.setShowInitial, onMouseOver: this.setShowInitial, onFocus: this.debouncedOpen, className: containerClassName }, restProps), this.props.children));
    }
}
Tooltip.propTypes = {
    children: PropTypes.node.isRequired,
    contents: PropTypes.node,
    direction: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    show: PropTypes.bool,
    onClickOutside: PropTypes.func,
    style: StylePropType,
    tooltipStyle: StylePropType,
    tooltipInnerStyle: StylePropType,
    className: PropTypes.string,
    containerClassName: PropTypes.string,
    variant: PropTypes.oneOf(['tooltip', 'popover']),
    constraints: PropTypes.arrayOf(PropTypes.object),
    delay: PropTypes.number,
    variety: deprecated(PropTypes.oneOf(['tooltip', 'popover']), 'Use `variant` instead.'),
};
Tooltip.defaultProps = {
    direction: 'top',
    className: '',
    containerClassName: '',
    tooltipStyle: {},
    tooltipInnerStyle: {},
    constraints: [
        {
            to: 'window',
            attachment: 'together',
            pin: ['left', 'right'],
        },
    ],
    delay: 0,
    style: {},
};
export default enhanceWithClickOutside(Tooltip);
